import {
  BlockChange,
  isBlockWithVariantOverrides,
} from '@basehub/data/replicache/index'
import {
  jsonToPlainText,
  stateToJson,
  richTextExtensionsForTransformations,
} from '@basehub/tiptap-api-utils'
import { invariant } from '@epic-web/invariant'
import { clsx } from 'clsx'
import { Input } from '~/sotano-kit/primitives/input'
import * as React from 'react'
import { InlineTextDiff } from '../rich-text/diff'
import { OverridesDiffWrapper } from '../../../../../components/diff/overrides-wrapper'
import { hasVariantOverrides } from '@basehub/data/replicache/index'

type TextBlockDiffProps = {
  condition: 'removed' | 'added'
  change: BlockChange
  type: 'text' | 'number'
}

export const TextBlockDiff = ({
  change,
  condition,
  type,
}: TextBlockDiffProps) => {
  const isConflictOnValue =
    change.status === 'conflict' && change.conflictPaths.includes('value')
  const beforeBlock = isConflictOnValue ? change.conflicting : change.previous

  const beforeState = React.useMemo(() => {
    if (beforeBlock?.type === 'rich-text') {
      invariant(type === 'text', 'Cannot diff rich-text with number type')
      return String(
        jsonToPlainText(
          stateToJson(beforeBlock.value.state),
          richTextExtensionsForTransformations
        ) ?? ''
      )
    }

    return String(beforeBlock?.value ?? '')
  }, [beforeBlock, type])

  const afterState = React.useMemo(() => {
    const isDeletedAndConditionIsAdded =
      condition === 'added' && change.status === 'deleted'
    if (isDeletedAndConditionIsAdded) {
      return String(change.previous?.value ?? '')
    }

    if (change.current?.type === 'rich-text') {
      invariant(type === 'text', 'Cannot diff rich-text with number type')
      return String(
        jsonToPlainText(
          stateToJson(change.current.value.state),
          richTextExtensionsForTransformations
        ) ?? ''
      )
    }

    return String(change.current?.value ?? '')
  }, [change, condition, type])

  return (
    <>
      <Input
        asChild
        className={clsx(
          condition === 'added' && change.status === 'deleted' && 'opacity-30',
          'whitespace-pre-wrap'
        )}
      >
        <InlineTextDiff
          className={clsx(
            condition === 'removed' && '!text-dark-muted',
            'py-2 !h-auto min-h-[39px]'
          )}
          text={afterState}
          textMatch={beforeState}
          condition={condition}
          isConflictingText={isConflictOnValue}
        />
      </Input>
      <TextOverrideDiff change={change} condition={condition} />
    </>
  )
}

type OverrideDiff = {
  current: string
  previous: string
  overrideKey: string
  className?: string
}

export const TextOverrideDiff = ({
  change,
  condition,
}: Omit<TextBlockDiffProps, 'type'>) => {
  const isConflictOnVariants =
    change.status === 'conflict' &&
    change.conflictPaths.includes('variantOverrides')
  const beforeBlock = isConflictOnVariants
    ? change.conflicting
    : change.previous

  const variantsToIterate: OverrideDiff[] = React.useMemo(() => {
    if (!change.current) {
      if (
        condition === 'removed' &&
        beforeBlock &&
        isBlockWithVariantOverrides(beforeBlock)
      ) {
        return Object.entries(beforeBlock.variantOverrides ?? {}).map(
          ([key, override]) => ({
            overrideKey: key,
            current: '',
            previous: String(override.value),
          })
        )
      }
      return []
    }

    if (!hasVariantOverrides(change.current)) return []
    const beforeOverrides =
      beforeBlock && isBlockWithVariantOverrides(beforeBlock)
        ? beforeBlock.variantOverrides
        : {}
    const _overrides = Object.entries(
      change.current.variantOverrides ?? {}
    ).map(([key, override]) => ({
      overrideKey: key,
      current: String(override.value ?? ''),
      previous: String(beforeOverrides?.[key]?.value || ''),
    }))

    if (condition === 'removed') {
      for (const [key, missingOverride] of Object.entries(
        beforeOverrides ?? {}
      )) {
        if (!_overrides.find((o) => key === o.overrideKey)) {
          _overrides.push({
            overrideKey: key,
            current: '',
            previous: String(missingOverride.value ?? ''),
          })
        }
      }
    }

    return _overrides
  }, [beforeBlock, change, condition])

  const Children = React.useCallback(
    (props: OverrideDiff) => (
      <Input
        asChild
        className={clsx(
          condition === 'added' && change.status === 'deleted' && 'opacity-30',
          'whitespace-pre-wrap',
          props.className
        )}
      >
        <InlineTextDiff
          className={clsx(
            condition === 'removed' && '!text-dark-muted',
            'py-2 !h-auto min-h-[39px]'
          )}
          text={props.current}
          textMatch={props.previous}
          condition={condition}
          isConflictingText={isConflictOnVariants}
        />
      </Input>
    ),
    [change.status, condition, isConflictOnVariants]
  )

  if (!variantsToIterate.length) return null

  return (
    <OverridesDiffWrapper<OverrideDiff> overrides={variantsToIterate}>
      {Children}
    </OverridesDiffWrapper>
  )
}
